import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

export default function AdminProvisioning() {
  const { token } = useAuthStore();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/admin/provisioning-jobs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      } else {
        setError('Failed to load provisioning jobs');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (jobId) => {
    try {
      const response = await fetch(`http://localhost:3001/api/admin/provisioning-jobs/${jobId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        fetchJobs();
      } else {
        setError('Failed to retry job');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading provisioning jobs...</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <a href="/admin" className="text-blue-600 hover:text-blue-800">
            ← Back to Admin
          </a>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Provisioning Jobs</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">Job ID</th>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">
                  Attempts
                </th>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">Created</th>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">Error</th>
                <th className="px-6 py-4 text-left text-gray-700 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-t border-gray-200">
                  <td className="px-6 py-4 text-sm font-mono">{job.id.slice(0, 8)}...</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded text-sm font-semibold ${getStatusColor(job.status)}`}>
                      {job.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {job.attemptCount} / 5
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {new Date(job.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-red-600">
                    {job.lastError ? job.lastError.slice(0, 30) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {job.status === 'failed' && (
                      <button
                        onClick={() => handleRetry(job.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {jobs.length === 0 && (
            <div className="text-center py-8 text-gray-600">No provisioning jobs found</div>
          )}
        </div>
      </div>
    </div>
  );
}
